/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';

import { type CommonProps, type HtmlAttributes } from '../_common/types';
import { useSemiControlledInput } from '../_common/use-semi-controlled-input';
import { needleWarningMessage } from '../_common/utils';

type CheckboxProps = {
  /** Whether the checkbox is checked */
  isChecked?: boolean;
  /** Checks the input by default in uncontrolled mode */
  isDefaultChecked?: boolean; // isDefaultChecked sounds weird to me?
  /** Whether the checkbox is indeterminate */
  isIndeterminate?: boolean;
  /** Whether the checkbox is disabled */
  isDisabled?: boolean;
  /** Whether the checkbox & label should take the full available width of its container */
  isFluid?: boolean;
  /** The value of the checkbox */
  value?: HtmlAttributes<'input'>['value']; // should this be required?
  /** Whether the label should be displayed before the checkbox */
  hasLeadingLabel?: boolean;
  /** The callback function triggered when the checkbox value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** The callback function triggered when the checkbox is clicked */
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
} & (
  | {
      /** The aria label of the checkbox. Required if the label is not a string. */
      ariaLabel: string;
      /** The label of the checkbox. If not a string, supply an aria label for screen reader support. */
      label?: undefined;
    }
  | {
      ariaLabel?: string;
      label: React.ReactNode;
    }
);

export const Checkbox = ({
  isChecked,
  isDefaultChecked,
  isIndeterminate = false,
  isFluid = false,
  isDisabled = false,
  hasLeadingLabel,
  ariaLabel,
  onChange,
  onClick,
  value,
  label,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'input', CheckboxProps>) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const mergedRefs = useMergeRefs([internalRef, ref]);

  const [isCheckedState, internalOnChange] = useSemiControlledInput({
    inputType: 'checkbox',
    isControlled: isChecked !== undefined,
    onChange,
    state: isChecked ?? isDefaultChecked ?? false,
  });

  const labelWrappingClasses = classNames('ndl-checkbox-label', {
    'ndl-disabled': isDisabled,
    'ndl-fluid': isFluid,
  });

  const checkboxClasses = classNames('ndl-checkbox', className, {
    'ndl-disabled': isDisabled,
  });

  useEffect(() => {
    if (internalRef.current) {
      internalRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  /** TODO: Label handling. We can get rid of this annoying stuff as soon as we do proper form control. */
  const labelContent = useMemo(() => {
    if (Boolean(label) === false) {
      return null;
    }

    if (typeof label === 'string') {
      return (
        <span title={label} className="ndl-checkbox-label-text">
          {label}
        </span>
      );
    }
    return label;
  }, [label]);

  useEffect(() => {
    if (Boolean(label) === false && Boolean(ariaLabel) === false) {
      needleWarningMessage(
        'Checkbox needs a valid label or ariaLabel: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }
  }, [ariaLabel, label]);

  return (
    <label className={labelWrappingClasses}>
      {hasLeadingLabel === true && labelContent}
      <input
        ref={mergedRefs}
        type="checkbox"
        aria-label={ariaLabel}
        disabled={isDisabled}
        checked={isCheckedState}
        onClick={onClick}
        onChange={internalOnChange}
        value={value}
        style={style}
        className={checkboxClasses}
        {...restProps}
        {...htmlAttributes}
      />
      {Boolean(hasLeadingLabel) === false && labelContent}
    </label>
  );
};
